/**
 * Party mode, shaped once for every screen that shows it: the TV strip, the
 * phone's line and the host panel. Pure — the caller passes `now`, so a
 * ticking component and a test read the same countdown.
 */
import {
  GAME_LABELS,
  MIN_PARTY_GAMES,
  PARTY_LIMITS,
  normalizePartyPlan,
  partyPlanProblem,
  partySlotMinutes,
  type GameType,
  type PartyPlan,
  type PublicParty,
} from '@tv-casino/shared';

export interface PartyStepVM {
  game: GameType;
  label: string;
  /** Where this game sits in the current lap. */
  state: 'played' | 'current' | 'upcoming';
}

export interface PartyVM {
  steps: PartyStepVM[];
  /** "Round 2 of 5", "12:04 left", "Switching after this hand", "Party's over". */
  progressLine: string;
  /** "Next: Roulette", or null when nothing follows. */
  nextLine: string | null;
  /** "Lap 2 of 3", "Lap 2", or null on the first lap of a looping party. */
  lapLine: string | null;
  /** Schedule mode: "1:24:10 left in the party". */
  endLine: string | null;
  /**
   * The TV header's pill, which stands with the host and seat chips: a small
   * gold label ("Party · Next Roulette") over a short value ("Round 2 of 5",
   * "12:04 left", "Last hand"). The kicker already names the game in play.
   */
  pillLabel: string;
  pillValue: string;
  due: boolean;
  done: boolean;
  /** True when the strip needs a clock ticking under it. */
  ticking: boolean;
}

/** 754_000 → "12:34"; 5_025_000 → "1:23:45". Rounds up, so 0:00 means now. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

export function partyModel(party: PublicParty | null | undefined, now: number): PartyVM | null {
  if (!party) return null;
  const current = party.slot % party.games.length;
  const steps = party.games.map((game, index) => ({
    game,
    label: GAME_LABELS[game],
    state: (index < current ? 'played' : index === current ? 'current' : 'upcoming') as PartyStepVM['state'],
  }));

  const schedule = party.mode === 'schedule';
  let progressLine: string;
  let pillValue: string;
  if (party.done) {
    progressLine = "That's the party — thanks for playing";
    pillValue = "Party's over";
  } else if (party.due) {
    progressLine = 'Switching after this hand';
    pillValue = 'Last hand';
  } else if (schedule && party.slotEndsAt !== null) {
    const left = formatClock(party.slotEndsAt - now);
    progressLine = `${left} left on ${GAME_LABELS[party.current]}`;
    pillValue = `${left} left`;
  } else {
    // The round in play counts, so the first hand reads "Round 1 of 5".
    const round = Math.min((party.roundsPlayed ?? 0) + 1, party.roundsPerGame ?? 1);
    progressLine = `Round ${round} of ${party.roundsPerGame}`;
    pillValue = progressLine;
  }

  const lapLine =
    party.laps !== null
      ? party.laps > 1
        ? `Lap ${party.lap} of ${party.laps}`
        : null
      : party.lap > 1
        ? `Lap ${party.lap}`
        : null;

  const next = party.done || !party.next ? null : GAME_LABELS[party.next];

  return {
    steps,
    progressLine,
    nextLine: next ? `Next: ${next}` : null,
    lapLine,
    pillLabel: ['Party', lapLine, next ? `Next ${next}` : null].filter(Boolean).join(' · '),
    pillValue,
    endLine:
      schedule && !party.done && party.endsAt !== null
        ? `${formatClock(party.endsAt - now)} left in the party`
        : null,
    due: party.due,
    done: party.done,
    ticking: schedule && !party.done,
  };
}

/** What the host is filling in before the party starts. */
export type PartyDraft = PartyPlan;

export const DEFAULT_PARTY_DRAFT: PartyDraft = normalizePartyPlan({ games: [] });

/** Toggle a game in or out of the lineup; new picks join the end. */
export function toggleDraftGame(draft: PartyDraft, game: GameType): PartyDraft {
  const games = draft.games.includes(game)
    ? draft.games.filter((g) => g !== game)
    : [...draft.games, game];
  return { ...draft, games };
}

/** "40 min each", "About 7 min each" — a minute count a host can read at a glance. */
function minutesLabel(minutes: number): string {
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
  }
  return Number.isInteger(minutes) ? `${minutes} min` : `about ${Math.round(minutes)} min`;
}

/** The line under the form: what pressing Start will do, or why it cannot. */
export function partyDraftSummary(draft: PartyDraft): { ok: boolean; line: string } {
  const plan = normalizePartyPlan(draft);
  if (plan.games.length < MIN_PARTY_GAMES) {
    return { ok: false, line: `Tap at least ${MIN_PARTY_GAMES} games, in the order to play them.` };
  }
  if (draft.mode === 'schedule' && draft.durationMinutes < PARTY_LIMITS.durationMinutes.min) {
    return { ok: false, line: 'Pick how long the party runs.' };
  }
  const problem = partyPlanProblem(plan);
  if (problem) return { ok: false, line: problem };
  if (plan.mode === 'rounds') {
    const each = plan.roundsPerGame === 1 ? '1 round' : `${plan.roundsPerGame} rounds`;
    return { ok: true, line: `${each} of each game, looping until you stop it.` };
  }
  const laps = plan.laps > 1 ? `, ${plan.laps} times through` : '';
  return {
    ok: true,
    line: `Each game gets ${minutesLabel(partySlotMinutes(plan))}${laps}. Switches wait for the hand to finish.`,
  };
}
